/**
 * ARTISTEN SOM DELES (D4, QA13 #100; 0936–0937).
 *
 * En manager jobber med HELE artisten — alle bookinger, forespørsler og bud — og ikke med én
 * konsert, som reisefølget. Derfor en egen delingstype, `artist`, med omfang og nivå:
 *
 *  * MALEN er organisasjonens («Eksterne managere ser …», 253.23). Den forhåndsutfyller dialogen og
 *    kan endres per person. Uten en rad gjelder `DEFAULT_ARTIST_SHARE_TEMPLATE`.
 *  * MOTTAKEREN leser artisten som et dokument fra `GET /v1/me/artists/:artistId/view`, bygget av
 *    definer-funksjonen `app.shared_artist_view_by_artist` (1131), som bare svarer den som har tilgang.
 *    Et område som ikke er delt, er `null` i svaret — basen sender det ikke.
 *  * ALDRI byråets provisjon, interne notater eller internchat (253.22). De har ingen kolonne her.
 */
import { z } from 'zod';

import {
  ARTIST_SHARE_PERMISSIONS,
  ARTIST_SHARE_SCOPES,
  BAND_SHARE_SCOPES,
  type ArtistSharePermission,
  type ArtistShareScope,
} from './shares.js';
import {
  artistAgreementDtoSchema,
  artistBlackoutDtoSchema,
  artistCommissionSchema,
} from './artist-agreement.js';
import { concertKindSchema } from './concerts.js';
import { wireInstant } from './wire-time.js';

export const artistShareScopeSchema = z.enum(ARTIST_SHARE_SCOPES).meta({ id: 'ArtistShareScope' });
export const artistSharePermissionSchema = z
  .enum(ARTIST_SHARE_PERMISSIONS)
  .meta({ id: 'ArtistSharePermission' });

/** 253.22: alt som kan deles, og «Kan se» (§5.2, 27.10: «Kan kommentere» er borte). */
export const DEFAULT_ARTIST_SHARE_TEMPLATE: Readonly<{
  scopes: readonly ArtistShareScope[];
  permission: ArtistSharePermission;
}> = Object.freeze({ scopes: [...ARTIST_SHARE_SCOPES], permission: 'view' });

export const artistShareTemplateSchema = z
  .object({
    scopes: z.array(artistShareScopeSchema).min(1).max(ARTIST_SHARE_SCOPES.length),
    permission: artistSharePermissionSchema,
    /** Om organisasjonen har lagret en mal, eller om standarden gjelder. */
    isDefault: z.boolean(),
  })
  .meta({ id: 'ArtistShareTemplate' });
export type ArtistShareTemplate = z.infer<typeof artistShareTemplateSchema>;

export const updateArtistShareTemplateRequestSchema = z
  .object({
    scopes: z.array(artistShareScopeSchema).min(1).max(ARTIST_SHARE_SCOPES.length),
    permission: artistSharePermissionSchema,
  })
  .meta({ id: 'UpdateArtistShareTemplateRequest' });
export type UpdateArtistShareTemplateRequest = z.infer<
  typeof updateArtistShareTemplateRequestSchema
>;

/**
 * «HELE BANDET» (276.11 c, 1059): standardsettet på artisten som innloggingene i bandet arver, med
 * mindre personen har egne avhukinger. Områdene er managementens uten «Bud og honorar» (A13).
 */
export const bandShareScopeSchema = z.enum(BAND_SHARE_SCOPES).meta({ id: 'BandShareScope' });
export const bandShareAccessSchema = z
  .object({
    scopes: z.array(bandShareScopeSchema).min(1).max(BAND_SHARE_SCOPES.length),
    permission: artistSharePermissionSchema,
  })
  .meta({ id: 'BandShareAccess' });
export type BandShareAccess = z.infer<typeof bandShareAccessSchema>;
export const updateBandShareAccessRequestSchema = bandShareAccessSchema.meta({
  id: 'UpdateBandShareAccessRequest',
});
export type UpdateBandShareAccessRequest = z.infer<typeof updateBandShareAccessRequestSchema>;

const day = z.iso.date();

/*
 * 1058: veien inn i Mine konserter, når `app.my_concert_access` slipper personen inn. Ingen ny dør:
 * lenken står bare der konsertsiden ville svart.
 */
const sharedArtistConcertLinkSchema = z
  .object({ kind: concertKindSchema, id: z.uuid() })
  .nullable()
  .default(null);
const optionalText = z.string().nullable().default(null);

export const sharedArtistBookingSchema = z
  .object({
    id: z.uuid(),
    startsOn: day,
    endsOn: day,
    organiserName: z.string(),
    venue: z.string().nullable(),
    status: z.string(),
    /** Honoraret, bare når «Bud og honorar» er delt. Aldri byråets provisjon. */
    feeMinor: z.number().int().nullable(),
    currency: z.string(),
    /*
     * DETALJENE (1058): raden kan åpnes. `default(null)`: et svar fra før migreringen har dem ikke,
     * og raden skal åpnes med det som finnes framfor å feile.
     */
    kind: concertKindSchema.nullable().default(null),
    stageName: optionalText,
    festivalName: optionalText,
    city: optionalText,
    /** Settiden, når arrangøren har lagt den inn. */
    startsAt: optionalText,
    endsAt: optionalText,
    timeZone: optionalText,
    formatName: optionalText,
    setLengthMinutes: z.number().int().nullable().default(null),
    concert: sharedArtistConcertLinkSchema,
  })
  .meta({ id: 'SharedArtistBooking' });

export const sharedArtistCalendarItemSchema = z
  .object({
    id: z.uuid(),
    /** En booking som legger beslag på datoen, eller en periode artisten ikke kan. */
    kind: z.enum(['booking', 'unavailable']),
    startsOn: day,
    endsOn: day,
    title: z.string(),
    /** Bookingens status (`confirmed`, `hold`, `tentative`); null for en utilgjengelig periode. */
    status: z.string().nullable(),
    /** 1058: bookingens detaljer, så kalenderraden kan åpnes. Null for en utilgjengelig periode. */
    booking: sharedArtistBookingSchema.nullable().default(null),
  })
  .meta({ id: 'SharedArtistCalendarItem' });

export const sharedArtistBidSchema = z
  .object({
    id: z.uuid(),
    counterpartyName: z.string(),
    kind: z.enum(['interest', 'bid']),
    performanceDate: day.nullable(),
    venueName: z.string().nullable(),
    amountMinor: z.number().int().nullable(),
    askingFeeMinor: z.number().int().nullable(),
    currency: z.string(),
    status: z.string(),
  })
  .meta({ id: 'SharedArtistBid' });

export const sharedArtistDocumentSchema = z
  .object({
    id: z.uuid(),
    kind: z.string(),
    label: z.string().nullable(),
    url: z.string().nullable(),
    /** Filen, når dokumentet har en. Lastes ned gjennom delingen, aldri fra byråets sider. */
    attachmentId: z.uuid().nullable(),
    filename: z.string().nullable(),
  })
  .meta({ id: 'SharedArtistDocument' });

export const sharedArtistAdvanceSchema = z
  .object({
    id: z.uuid(),
    organizationName: z.string(),
    performanceDate: day.nullable(),
    venueName: z.string().nullable(),
    /* 1058: programposten og veien inn, Mine konserter eller en deling av programposten (/delt). */
    performanceId: z.uuid().nullable().default(null),
    stageName: optionalText,
    festivalName: optionalText,
    startsAt: optionalText,
    endsAt: optionalText,
    timeZone: optionalText,
    concert: sharedArtistConcertLinkSchema,
    grantId: z.uuid().nullable().default(null),
  })
  .meta({ id: 'SharedArtistAdvance' });

/**
 * STAMDATAENE SOM IKKE ER INTERNE (A9, 1058), som byråets Artister-side. Bandet med navn, rolle og
 * instrument, aldri telefon, allergier eller notater. Formatprisen bare med «Bud og honorar».
 */
export const sharedArtistProfileSchema = z
  .object({
    artistType: optionalText,
    biography: optionalText,
    biographySource: z.enum(['agency', 'soundcharts']).nullable().default(null),
    links: z
      .object({
        spotifyUrl: optionalText,
        instagramHandle: optionalText,
        tiktokUrl: optionalText,
        youtubeUrl: optionalText,
      })
      .default({ spotifyUrl: null, instagramHandle: null, tiktokUrl: null, youtubeUrl: null }),
    agent: z
      .object({ name: z.string(), phone: z.string().nullable(), email: z.string().nullable() })
      .nullable()
      .default(null),
    management: z
      .array(
        z.object({
          name: z.string(),
          role: z.string().nullable(),
          phone: z.string().nullable(),
          email: z.string().nullable(),
        }),
      )
      .default([]),
    members: z
      .array(
        z.object({
          name: z.string(),
          role: z.string().nullable(),
          instrument: z.string().nullable(),
        }),
      )
      .default([]),
    formats: z
      .array(
        z.object({
          id: z.uuid(),
          name: z.string(),
          isDefault: z.boolean(),
          travellingParty: z.number().int().nullable(),
          setLengthMinutes: z.number().int().nullable(),
          description: z.string().nullable(),
          askingFeeMinor: z.number().int().nullable(),
          currency: z.string(),
        }),
      )
      .default([]),
  })
  .meta({ id: 'SharedArtistProfile' });

/**
 * AVTALE-FANEN FOR MOTTAKEREN (persontypene §2.1, 1131): avtalen mellom artist og byrå, sperreperiodene
 * og avtaledokumentene. Bare med hele artisten og økonomi; ellers er feltet null.
 */
export const sharedArtistAgreementSchema = z
  .object({
    agreement: artistAgreementDtoSchema.nullable(),
    blackouts: z.array(artistBlackoutDtoSchema),
    documents: z.array(sharedArtistDocumentSchema),
    /**
     * 292.1: årets provisjon, den samme som byrået ser under satsen. `default(null)`: et svar fra før
     * 1146 har den ikke.
     */
    commission: artistCommissionSchema.nullable().default(null),
  })
  .meta({ id: 'SharedArtistAgreement' });
export type SharedArtistAgreement = z.infer<typeof sharedArtistAgreementSchema>;

export const sharedArtistViewSchema = z
  .object({
    /**
     * Personens egen deling. Null når artisten er lest etter tilgangen uten en egen deling, som for
     * en admin i en mottakerorganisasjon (`GET /v1/me/artists/:artistId/view`, 1131).
     */
    grantId: z.uuid().nullable(),
    organizationName: z.string(),
    artistName: z.string(),
    /*
     * Artisthodet, som byråets (A9, 1038). `default(null)`: et svar fra før migreringen har dem
     * ikke, og siden skal tegne hodet uten faktalinje framfor å feile.
     */
    genre: z.string().nullable().default(null),
    homeTown: z.string().nullable().default(null),
    imageUrl: z.string().nullable().default(null),
    assigneeName: z.string().nullable().default(null),
    permission: artistSharePermissionSchema,
    scopes: z.array(artistShareScopeSchema),
    expiresAt: wireInstant.nullable(),
    contact: z
      .object({ name: z.string(), phone: z.string().nullable(), email: z.string().nullable() })
      .nullable(),
    /** Samtalen med byrået (delingstråden), når «Samtale» er delt. */
    threadId: z.uuid().nullable(),
    /** Oversikt (1058). Null i et svar fra før migreringen. */
    profile: sharedArtistProfileSchema.nullable().default(null),
    calendar: z.array(sharedArtistCalendarItemSchema).nullable(),
    bookings: z.array(sharedArtistBookingSchema).nullable(),
    bids: z.array(sharedArtistBidSchema).nullable(),
    documents: z.array(sharedArtistDocumentSchema).nullable(),
    advances: z.array(sharedArtistAdvanceSchema).nullable(),
    /** Avtale-fanen (1131). `default(null)`: et svar fra før migreringen har den ikke. */
    agreement: sharedArtistAgreementSchema.nullable().default(null),
  })
  .meta({ id: 'SharedArtistView' });
export type SharedArtistView = z.infer<typeof sharedArtistViewSchema>;
export type SharedArtistBooking = z.infer<typeof sharedArtistBookingSchema>;
export type SharedArtistBid = z.infer<typeof sharedArtistBidSchema>;
export type SharedArtistCalendarItem = z.infer<typeof sharedArtistCalendarItemSchema>;
export type SharedArtistDocument = z.infer<typeof sharedArtistDocumentSchema>;
export type SharedArtistAdvance = z.infer<typeof sharedArtistAdvanceSchema>;
export type SharedArtistProfile = z.infer<typeof sharedArtistProfileSchema>;

/**
 * DELTE ARTISTER I SIDEMENYEN (A9, 268.2 a). Artistene som er delt med meg, på tvers av
 * organisasjonene (`app.my_shared_artists()`), og de delte bookingene: programpostene som er delt med
 * meg (`app.my_shares()`, typen `performance`). Detaljen leses fra `GET /v1/me/artists/:artistId/view`.
 */
export const mySharedArtistSchema = z
  .object({
    grantId: z.uuid(),
    organizationId: z.uuid(),
    organizationName: z.string(),
    artistName: z.string(),
    permission: artistSharePermissionSchema,
    scopes: z.array(artistShareScopeSchema),
    sharedAt: z.iso.datetime(),
  })
  .meta({ id: 'MySharedArtist' });
export type MySharedArtist = z.infer<typeof mySharedArtistSchema>;

export const mySharedBookingSchema = z
  .object({
    grantId: z.uuid(),
    organizationName: z.string(),
    title: z.string(),
    startsAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'MySharedBooking' });
export type MySharedBooking = z.infer<typeof mySharedBookingSchema>;

export const mySharedArtistListSchema = z
  .object({
    items: z.array(mySharedArtistSchema),
    /**
     * 286.3 (Atle 28.10): ALLTID TOM. En programpost (eller noe annet) fra en arrangør er en produksjon
     * og står under Delte produksjoner (`/me/shares/productions`); Delte artister er artistene fra et
     * byrå eller et management. Feltet står igjen så eldre klienter ikke velter; det fjernes med neste
     * kontraktsrydding.
     */
    bookings: z.array(mySharedBookingSchema),
  })
  .meta({ id: 'MySharedArtistList' });
export type MySharedArtistList = z.infer<typeof mySharedArtistListSchema>;
